// NEW_FILE_JUSTIFIED: Sub-components extracted from TelegramPersonalConnect to meet 200-line limit
import { useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import type {
  ITelegramPhoneFormProps,
  ITelegramOtpFormProps,
  ITelegramConnectedViewProps,
} from '@/@types/telegram';

export function TelegramPhoneForm({ onSubmit, isLoading }: ITelegramPhoneFormProps) {
  const phoneRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    phoneRef.current?.focus();
  }, []);

  return (
    <form onSubmit={onSubmit} className="flex gap-2">
      <Input
        ref={phoneRef}
        name="phone"
        type="tel"
        placeholder="+1 555 000 0000"
        autoComplete="tel"
        aria-label="Phone number"
        className="h-9 text-sm"
        disabled={isLoading}
        required
      />
      <Button type="submit" size="sm" disabled={isLoading} className="shrink-0 text-xs">
        Connect
      </Button>
    </form>
  );
}

export function TelegramOtpForm({ step, onSubmit, onCancel, isLoading }: ITelegramOtpFormProps) {
  const otpRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    otpRef.current?.focus();
  }, []);

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-2">
      <p className="text-xs text-muted-foreground">Check your Telegram for a verification code.</p>
      <div className="flex gap-2">
        <Input
          ref={otpRef}
          name="otp"
          type="text"
          inputMode="numeric"
          placeholder="123456"
          autoComplete="one-time-code"
          aria-label="Verification code"
          maxLength={8}
          className="h-9 text-sm"
          disabled={isLoading}
          required
        />
        <Button type="submit" size="sm" disabled={isLoading} className="shrink-0 text-xs">
          Verify
        </Button>
      </div>
      {step === 'awaiting_2fa' && (
        <Input
          name="password"
          type="password"
          placeholder="Cloud password (2FA)"
          autoComplete="current-password"
          aria-label="Cloud password"
          className="h-9 text-sm"
        />
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onCancel}
        className="self-start text-xs text-muted-foreground"
      >
        Cancel
      </Button>
    </form>
  );
}

export function TelegramConnectedView({ phoneNumber, onDisconnect }: ITelegramConnectedViewProps) {
  return (
    <div className="flex items-center justify-between">
      <p className="text-xs text-muted-foreground">
        Connected as <span className="font-medium text-foreground">{phoneNumber}</span>
      </p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onDisconnect}
        className="text-xs text-destructive border-destructive/40 hover:bg-destructive/10"
      >
        Disconnect
      </Button>
    </div>
  );
}

export function TelegramConnectingSpinner() {
  return (
    <div
      role="status"
      aria-label="Connecting to Telegram"
      className="flex items-center gap-2 text-xs text-muted-foreground"
    >
      <Loader2 size={13} className="animate-spin" aria-hidden="true" />
      <span>Connecting...</span>
    </div>
  );
}
